(ns nosco.ui.inputs
  (:require
   ["react" :as React]
   [clipboard-polyfill]
   [clojure.string :as string]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc] :as hx]
   [nosco.colors :as colors]
   [nosco.hooks]
   [nosco.i18n.translate :as t]
   [nosco.icons :as icons]
   [nosco.icons2 :as icons2]
   [nosco.lazy]
   [nosco.react :as nosco-react]
   [nosco.richtext.api :as richtext-api]
   [nosco.richtext.editor-skeleton :as editor-skeleton]
   [nosco.shared.submission-form :as submission-form]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.html :as html]
   [nosco.ui.layouts :as layouts]
   [nosco.ui.loading :as loading]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [nosco.ui.toast :as toast]
   [nosco.util :as util]
   [nosco.util.format :as nosco-format]))

(def HTMLEditor (nosco.lazy/lazy-component nosco.richtext.editor/HTMLEditor))

;; Sometimes a field has an error but we don't want to display an
;; additional error message, for example when the field already has
;; an "error" layout precisely pointing to the error
(defn input-error [error-message]
  {:error-message error-message})

(defnc ^:private TextInputIcon [{:keys [left children] :as props}]
  [:div
   {:class ["nosco-text-input-icon-outer"
            (when left "nosco-text-input-icon-outer--left")]
    :style {:pointer-events "none"}}
   (or children
       [icons2/Icon
        (-> {:class ["nosco-text-input-icon"]}
            (nosco-react/merge-props (dissoc props :left)))])])

(defnc TextInput
  "An input element wrapped in a div.

  Props:
  - :inline - render the container as an inline element
  - :error - render with an error state
  - :size - :small or :xsmall for compact variants
  - :icon - keyword or React element, an icon for the input
  - :left-icon - ditto, on the left
  - :focus-on-mount? - The :autoFocus property does not work inside modals. Use :focus-on-mount? as an alternative (based on refs)"
  [{:keys [inline error size left-icon icon focus-on-mount?] :as props} ref]
  [:div
   {:class ["nosco-text-input-outer"
            (when inline "d-inline-block")]}
   (when left-icon
     (if-not (React/isValidElement left-icon)
       [TextInputIcon {:icon left-icon}]
       left-icon))
   [:input
    (-> {:ref (nosco-react/merge-refs ref (when focus-on-mount? util/focus-callback))
         :data-form-field-interactive true
         :class ["nosco-text-input"
                 (when-not inline "w-100")
                 (when (= size :small) "nosco-text-input--small")
                 (when (= size :xsmall) "nosco-text-input--xsmall")
                 (when left-icon "nosco-text-input--with-left-icon")
                 (when icon "nosco-text-input--with-icon")
                 (when error "nosco-text-input--error")]}
        (nosco-react/merge-props
         (dissoc props
                 :inline :message :error :size :left-icon :icon :focus-on-mount?)))]
   (when icon
     (if-not (React/isValidElement icon)
       [TextInputIcon {:icon icon}]
       icon))])

(nosco-react/forward-ref! TextInput)

(defn valid-input-number [s]
  (re-matches nosco-format/valid-number-re s))

(defnc NumberInput
  "A controller numeric input that manages a number value. Uses
   the nosco.util.format/platform-number-locale to reject invalid input,
   and to parse/format the string typed into a number.
   On blur, it will force-format the value into a string, to make sure
   the user sees the actual value entered."
  [{:keys [value set-value]
    :as props} ref]
  (let [[s set-s] (hooks/useState (nosco-format/format-number-for-input value))
        input-ref (React/useRef)
        props (dissoc props :set-value :value)]
    ;; Sync internal state when value changes externally (e.g., from slider dragging)
    ;; Only update if the input is not focused, to avoid overwriting user typing
    (hooks/useEffect
     (fn []
       (when-not (= (.-activeElement js/document) (.-current input-ref))
         (set-s (nosco-format/format-number-for-input value))))
     [value])
    (when (contains? props :on-blur)
      (throw (ex-info "Cannot use on-blur on NumberInput." {})))
    (when (contains? props :on-change)
      (throw (ex-info "Cannot use on-change on NumberInput, use set-value instead." {})))
    [TextInput (assoc props
                      :ref (nosco-react/merge-refs ref input-ref)
                      :type "text"
                      :inputMode "numeric"
                      :value (str s)
                      :on-change
                      (fn [e]
                        (let [s (-> e .-target .-value)]
                          (when (valid-input-number s)
                            (set-s s)
                            (set-value (nosco-format/locale-number-parse s)))))
                      :on-blur (fn []
                                 (set-s (when value (nosco-format/format-number-for-input value)))))]))

(nosco-react/forward-ref! NumberInput)

(defnc LargeTextInput
  "A large text input which expands to show the text on multiple lines."
  [{:keys [value read-only on-change placeholder]} ref]
  {:wrap [(React/forwardRef)]}
  (let [textbox-ref (or ref (React/useRef))]
    (hooks/useLayoutEffect
     (fn []
       (when-let [el (.. textbox-ref -current)]
         (set! (.-innerText el) value))
       (fn []))
     [])
    [:div
     {:ref textbox-ref
      :role "textbox"
      :contentEditable (not read-only)
      :data-form-field-interactive (not read-only)
      :class ["nosco-text-input"
              "nosco-text-input--heading-large"
              "nosco-text-input--no-border"]
      :data-placeholder (when (string/blank? value) placeholder)
      :on-key-press
      (fn [event]
        ;; Prevent entering multiple lines
        (when (= "Enter" (.-key event))
          (.preventDefault event)))
      :on-input
      (fn []
        (on-change (.. textbox-ref -current -innerText)))}]))

;; TODO: Add label props to standard input components
(defnc LabelledTextInput
  "A text input with an optional label and message.

  Props:
  - :inline - render the container as an inline element
  - :label - the contents of the label element
  - :message - the contents of the message

  Props are passed to TextInput."
  [{:keys [inline label message required] :as props} ref]
  (let [id (nosco.hooks/use-id (:id props) "LabelledTextInput")]
    [:div {:class [(when inline "d-inline-block")]}
     (when label
       [:label
        {:class ["nosco-label"]
         :for id}
        label
        (when required
          [:span {:class "text-red ml-"} " *"])])
     [TextInput
      (-> {:ref ref
           :id id}
          (nosco-react/merge-props (dissoc props :id :inline :label :message :required)))]
     (when message
       [:div
        {:class ["nosco-labelled-text-input__message"
                 (if (:error props) "text-red" "text-gray-700")]}
        message])]))

(nosco-react/forward-ref! LabelledTextInput)

(defnc ^:private SearchInputIcon [{:keys [size]}]
  [TextInputIcon
   {:class ["nosco-search-input-icon"
            (when (= :small size) "nosco-search-input-icon--small")]
    :icon icons2/Search-small
    :aria-hidden true
    :left true}])

(defnc SearchInputLoadingIcon []
  [TextInputIcon
   [icons/LoadingIcon {:class "nosco-text-input-icon"}]])

(defnc ^:private SearchInputClearButton [{:keys [on-click on-mouse-down]}]
  [TextInputIcon
   [:button {:class ["button-reset" "nosco-search-input-clear-btn"]
             :on-click on-click
             :on-mouse-down on-mouse-down}
    [icons2/Icon {:class ["nosco-text-input-icon"] :icon icons2/Remove-small}]]])

(defn- set-input-value!
  "Sets the value of an HTMLInputElement and triggers the change event."
  [input-el value]
  ;; React overrides the value setter of inputs, so to ensure the native event has
  ;; the new value, get the native value setter and call it with the input-el.
  ;; This essentially emulates a native input event, and *should* work into new React versions.
  (let [value-setter (.-set (js/Object.getOwnPropertyDescriptor js/HTMLInputElement.prototype "value"))]
    (.call value-setter input-el value)
    (.dispatchEvent input-el (js/Event. "input" #js {:bubbles true}))))

(defnc SearchInput
  "Props:

  - :loading - render with a loading icon
  - :gray - render with a gray style

  Props are passed to TextInput."
  [{:keys [loading gray size] :or {size :small} :as props} ref]
  (let [[has-value ->has-value] (hooks/useState #(or (util/non-blank (:value props)) (util/non-blank (:default-value props))))
        input-ref (React/useRef)]
    (hooks/useEffect
     (fn []
       (when (:value props)
         (->has-value (some? (util/non-blank (:value props))))))
     [(:value props)])
    [TextInput
     (-> {:ref (nosco-react/merge-refs input-ref ref)
          :class ["nosco-search-input"
                  (when (= :small size) "nosco-text-input--small")
                  (when (= :xsmall size) "nosco-text-input--xsmall")
                  (when gray "nosco-search-input--gray")]
          :left-icon (hx/f [SearchInputIcon {:size size}])
          :icon (cond
                  loading
                  (hx/f [SearchInputLoadingIcon])

                  has-value
                  (hx/f [SearchInputClearButton
                         {:on-click
                          (fn [_]
                            (set-input-value! (.-current input-ref) "")
                            (.. input-ref -current (focus)))
                          ;; Prevent the blur event on the input element
                          :on-mouse-down (fn [e]
                                           (.preventDefault e)
                                           (.stopPropagation e))}]))
          :on-change
          (fn [^js event]
            (->has-value (some? (util/non-blank (.. event -target -value))))
            (when (:on-change props)
              ((:on-change props) event)))}
         (nosco-react/merge-props (dissoc props :loading :gray :size :on-change)))]))

(nosco-react/forward-ref! SearchInput)

(defnc SearchInputWithButton
  "Props:

  - :inline - render the container as an inline element
  - :button - An element for the button
  - :input - An element for the input
  - :popup - An element for a popup which will be rendered after the input"
  [{:keys [max-width inline button input popup] :or {max-width "36rem"} :as props}]
  [:div
   (-> {:class ["nosco-search-input-with-btn"
                (if inline "d-inline-flex" "d-flex")]
        :style {:max-width max-width}}
       (nosco-react/merge-props (dissoc props :max-width :inline :button :input :popup)))
   button
   [:div {:class ["nosco-search-input-with-btn__input-container"]}
    input
    popup]])

(defnc ExpandableSearchInput
  "Search button that expands into a pill-shaped text input on click.
   Collapses to a 24px icon button; expands to 320px pill on click.
   Collapses on Escape/click-outside. Clear button clears value and collapses."
  [{:keys [placeholder value on-change]}]
  (let [[expanded set-expanded] (hooks/useState false)
        has-value? (boolean (util/non-blank value))
        wrapper-ref (React/useRef nil)
        input-ref (React/useRef nil)
        expand (fn []
                 (set-expanded true)
                 (js/setTimeout #(when-let [el (.-current input-ref)]
                                   (.focus el)) 80))
        collapse (fn []
                   (set-expanded false))
        clear (fn []
                (when on-change (on-change ""))
                (collapse))]
    ;; Click-outside listener
    (hooks/useEffect
     (fn []
       (when expanded
         (let [handler (fn [^js e]
                         (when (and (.-current wrapper-ref)
                                    (not (.contains (.-current wrapper-ref) (.-target e))))
                           (collapse)))]
           (.addEventListener js/document "mousedown" handler)
           (fn [] (.removeEventListener js/document "mousedown" handler)))))
     [expanded])
    [:div {:ref wrapper-ref
           :class ["nosco-expandable-search"
                   (when has-value? "nosco-expandable-search--has-value")
                   (when expanded "nosco-expandable-search--expanded")]}
     ;; Search icon
     [:button {:class "nosco-expandable-search__icon"
               :type "button"
               :on-click (fn [^js e]
                           (when-not expanded
                             (.stopPropagation e)
                             (expand)))}
      [icons2/Icon {:icon icons2/Search-small}]]
     ;; Text input
     [:input {:ref input-ref
              :class "nosco-expandable-search__input"
              :type "text"
              :placeholder (or placeholder "")
              :auto-complete "off"
              :spell-check false
              :value (or value "")
              :on-change (fn [^js e]
                           (when on-change
                             (on-change (.. e -target -value))))
              :on-key-down (fn [^js e]
                             (when (= "Escape" (.-key e))
                               (collapse)))}]
     ;; Clear button
     [:button {:class "nosco-expandable-search__clear"
               :type "button"
               :tab-index -1
               :on-click (fn [^js e]
                           (.stopPropagation e)
                           (clear))}
      [icons2/Icon {:icon icons2/Remove-small}]]]))

(defnc TextArea [{:keys [label message id error value required? set-value max-length container-props] :as props}]
  (let [id (nosco.hooks/use-id id "TextAreaCommit")
        chars-count (util/char-count (str value))]
    [:div container-props
     (when label
       [:label {:class "nosco-label"
                :for id}
        label
        (when required?
          [:span {:class "ml-spacing-4 text-color-required"}

           "*"])])
     [:textarea
      (->
       {:id id
        :class ["nosco-text-area__input"
                (when error "nosco-text-area__input--error")]
        :value (str value)
        ;; textarea seems to have a native max-length;
        ;; but we don't use it to be able to show how many characters are over
        ;; -- we can add a max-length-strict prop if we want to use the native one
        :on-change (fn [e]
                     (let [v (.. e -currentTarget -value)]
                       (set-value v)))}
       (nosco-react/merge-props (dissoc props :message :label :value :set-value :id :required? :container-props :max-length)))]
     (when max-length
       [:div {:class "nosco-form-field__help-textarea"}
        [icons2/Icon {:icon icons2/Info-small
                      :color colors/neutral-400
                      ;; can't define width, height in css
                      ;; because icons2/Icon internally
                      ;; defines width, height inline
                      ;; which takes precedence
                      :icon-style {:width 12
                                   :height 12}}]
        [text/Text {:variant text/Body-Sm-Regular
                    :class "nosco-form-field__help-text"
                    :el :div}
         (t/t "Max. %s characters" max-length)]
        (when chars-count
          (let [over (- chars-count max-length)]
            [text/Text {:variant text/Body-Sm-Regular
                        :class ["nosco-form-field__help-text-details"
                                (when (pos? over)
                                  "text-red")]
                        :el :div}
             (if (pos? over)
               (t/tp ["%s character over" "%s characters over"]
                     over
                     (str over))
               (t/tp ["%s character left" "%s characters left"]
                     (- over)
                     (str (- over))))]))])
     (when message
       [:div
        {:class ["nosco-labelled-text-input__message"
                 (if (:error props) "text-red" "text-gray-700")]}
        message])]))

(defn- html-editor-chars-count [html-editor]
  (richtext-api/get-char-count html-editor))

(defnc RichTextArea [{:keys [label
                             message
                             id
                             default-value
                             set-value
                             set-meta
                             max-length
                             editor-ref
                             text-style
                             no-border] :as props}]
  (when (:value props)
    (throw (ex-info "Cannot use value in RichTextArea, use default-value instead." {})))
  (let [id (nosco.hooks/use-id id "RichTextAreaCommit")
        html-editor-ref (or editor-ref (React/useRef))
        [chars-count set-chars-count] (hooks/useState 0)]
    [:div
     (when label
       [:label {:class "nosco-label"
                :for id}
        label])
     ;; add a suspense here so that we don't suspend the entire page when loading
     ;; the editor
     (let [toolbar-layout [(if (= text-style :document)
                             :text-size--document
                             :text-size) :text-style
                           :paragraph-style :links :images
                           :files :videos]]
       [React/Suspense {:fallback (hx/f [editor-skeleton/EditorSkeleton
                                         {:no-border no-border
                                          :toolbar-layout toolbar-layout
                                          :initial-html default-value
                                          :content-class (when (= text-style :document)
                                                           "nosco-redactor-text--document")
                                          :content-style (:style props)}])}
        [HTMLEditor
         (->
          {:class ["nosco-rich-text-area__input"
                   (when (= text-style :document)
                     "nosco-redactor-text--document")]
           :initial-html (str default-value)
           :toolbar-layout toolbar-layout
           :on-create (fn []
                        (when max-length
                          (let [html-editor (.-current html-editor-ref)
                                chars-count (html-editor-chars-count html-editor)]
                            (set-chars-count chars-count)
                            (when set-meta
                              (set-meta {:chars-count chars-count})))))
           :on-change (fn [value]
                        (when set-value
                          (set-value value))
                        (when max-length
                          (let [html-editor (.-current html-editor-ref)
                                chars-count (html-editor-chars-count html-editor)]
                            (set-chars-count chars-count)
                            (when set-meta
                              (set-meta {:chars-count chars-count})))))
           :ref html-editor-ref
           :no-border no-border
           :max-length max-length}
          (nosco-react/merge-props
           (dissoc props :label :message :default-value :set-value :no-border)))]])
     (when max-length
       [:div {:class "nosco-form-field__help-textarea"}
        [icons2/Icon {:icon icons2/Info-small
                      :color colors/neutral-400
                      ;; can't define width, height in css
                      ;; because icons2/Icon internally
                      ;; defines width, height inline
                      ;; which takes precedence
                      :icon-style {:width 12
                                   :height 12
                                   :margin-top 3}}]
        [text/Text {:variant text/Body-Sm-Regular
                    :class "nosco-form-field__help-text"
                    :el :div}
         (t/t "Max. %s characters" max-length)]
        (when chars-count
          (let [over (- chars-count max-length)]
            [text/Text {:variant text/Body-Sm-Regular
                        :class ["nosco-form-field__help-text-details"
                                (when (pos? over)
                                  "text-red")]
                        :el :div}
             (if (pos? over)
               (t/tp ["%s character over" "%s characters over"]
                     over
                     (str over))
               (t/tp ["%s character left" "%s characters left"]
                     (- over)
                     (str (- over))))]))])
     (when message
       [:div
        {:class ["nosco-labelled-text-input__message"
                 (if (:error props) "text-red" "text-gray-700")]}
        message])]))

(defnc FormField
  [{:keys [label
           label-color
           variant
           error
           help-text
           required?
           description
           label-icon
           label-extra
           label-button
           children]
    :or {variant :default}}]
  (let [[label-variant description-variant] (case variant
                                              :xlarge [text/Heading-Sm :normal]
                                              :large [text/Body-Md-Bold :normal]
                                              :default [text/Label-Field-Label :small])]
    [:div {:class "nosco-form-field"}
     (when label
       [:div {:class "nosco-form-field__label"}
        label-icon
        [text/Text (cond-> {:variant label-variant
                            :el :label
                            :class "m-0"} ; reboot.scss adds a margin to labels :/
                     label-color (assoc :style {:color label-color}))
         label]
        (when required?
          [:span {:class "nosco-form_field__label-required"}
           "*"])
        label-extra
        (when label-button
          [:div {:class "nosco-form-field__label-button"}
           label-button])])
     (when-not (string/blank? description)
       [:div {:class "nosco-form-field__description"}
        [html/SafeHTMLEditor {:tag :span
                              :variant description-variant
                              :html description}]])
     ;; checkbox and radios might need an extra margin-left of 2px
     ;; TODO investigate if it's possible to do this
     (when children
       [:<>
        (cond
          (and (string/blank? label)  (string/blank? description))
          ;; remove extra padding but keep 1px to avoid clipping the outline of a focused element
          [:div {:style {:padding-bottom "1px"}}]
          (string/blank? description)
          [:div {:class "pb-spacing-8"}])

        (React/cloneElement children (if (some? error)
                                       #js {:error error}
                                       #js {}))])
     (cond
       (:error-message error)
       [:div {:class "nosco-form-field__error"}
        [icons2/Icon {:icon icons2/Semantic-Error-small
                      :color colors/error-500
                      ;; can't define width, height in css
                      ;; because icons2/Icon internally
                      ;; defines width, height inline
                      ;; which takes precedence
                      :icon-style {:width 12
                                   :height 12

                                   ;; optically center
                                   #_#_:margin-top 3}}]
        [text/Text {:variant text/Body-Sm-Regular
                    :class "nosco-form-field__error-text"
                    :el :div}
         (:error-message error)]]

       help-text
       [:div {:class "nosco-form-field__help"}
        [text/Text {:variant text/Body-Sm-Regular
                    :class "nosco-form-field__help-text"
                    :el :div}
         help-text]])]))

(defnc FormFieldDecoration [{:keys [label border-color bg-color text-color]}]
  [:div {:class "nosco-form-field-box-decoration"
         :style {"--decoration-color" border-color
                 "--decoration-bg-color" bg-color
                 "--decoration-text-color" text-color}}
   [:div {:class "nosco-form-field-box-decoration--inner"}
    label]])

(defnc FormFieldBox [{:keys [children error top-border decoration-type decoration-label] :as props}]
  [:div (nosco-react/merge-props
         {:class ["nosco-form-field-box"
                  (when error "nosco-form-field-box--error")
                  (when top-border "nosco-form-field-box--top-border")]
          :style (when (and decoration-type decoration-label (not error))
                   {"--border-style" (when decoration-type "solid")
                    "--border-color-override" (case decoration-type
                                                ::submission-form/addition colors/border-success
                                                ::submission-form/removal colors/border-error
                                                ::submission-form/stage-hidden colors/border-secondary)})}
         (dissoc props :children :error :top-border :decoration-type :decoration-label))
   (when (and decoration-type decoration-label)
     [FormFieldDecoration {:label decoration-label
                           :text-color (case decoration-type
                                         ::submission-form/addition colors/border-success
                                         ::submission-form/removal colors/border-error
                                         ::submission-form/stage-hidden colors/text-primary)
                           :bg-color (case decoration-type
                                       ::submission-form/addition colors/success-50
                                       ::submission-form/removal colors/error-50
                                       ::submission-form/stage-hidden colors/neutral-20)
                           :border-color (case decoration-type
                                           ::submission-form/addition colors/border-success
                                           ::submission-form/removal colors/border-error
                                           ::submission-form/stage-hidden colors/border-secondary)}])
   (React/cloneElement children #js {:error error})])

(defnc FormFieldOptionsGrid [{:keys [children]}]
  [:div {:class "nosco-form-field-options-grid"}
   [:<>
    children]])

(defnc Combobox
  "An accessible, mostly headless combobox.

  Props:
  - :input - React element for the text input
  - :items - Collection of items
  - :render-item - Function to render an item
  - :on-select - Called with the selected item, or nil when cleared
  - :listbox-style - The style of the listbox element
  - :loading - show a loading state while results are fetched

  The render-item function takes a map of :item, :id, :active, :on-select.
  It should return an element with the ARIA role `option`."
  [{:keys [input items render-item on-select listbox-style loading]}]
  (let [input-wrapper-ref (React/useRef nil)
        [input-wrapper-ref-cb popper] (popup/use-popper {:placement "bottom-start" :use-ref-width? true})
        [items-local ->items-local] (hooks/useState items)
        listbox-open (boolean (or loading (not (empty? items-local))))
        [active-item ->active-item] (hooks/useState nil)
        items-with-ids (hooks/useMemo (fn [] (map (juxt identity #(str (gensym "ComboboxItem"))) items-local)) [items-local])
        item->id (hooks/useMemo #(into {} items-with-ids) [items-with-ids])
        listbox-id (nosco.hooks/use-id "ComboboxListbox")
        popup-ref (React/useRef)]
    (hooks/useEffect
     (fn []
       (->active-item nil)
       (->items-local items))
     [items])
    (popup/use-click-outside listbox-open
                             (fn [^js event]
                               (when-not (.. input-wrapper-ref -current (contains (.-target event)))
                                 (->items-local nil)))
                             popup-ref)
    (popup/use-escape listbox-open #(->items-local nil))
    [:<>
     [:div
      {:ref (nosco-react/merge-refs input-wrapper-ref input-wrapper-ref-cb)
       :style {:position "relative"}
       :role "combobox"
       :aria-haspopup "listbox"
       :aria-owns listbox-id
       :aria-expanded listbox-open
       :on-key-down
       (fn [^js event]
         (when (#{"ArrowDown" "ArrowUp" "Enter" "Escape"} (.-key event))
           (when-not (empty? items-local)
             (let [active-item-idx (when active-item (util/index-of items-local active-item))]
               (case (.-key event)
                 "ArrowDown"
                 (->active-item (nth items-local (mod (inc (or active-item-idx -1)) (count items-local))))
                 "ArrowUp"
                 (->active-item (nth items-local (mod (dec (or active-item-idx (count items-local))) (count items-local))))
                 "Enter"
                 (when active-item (on-select active-item))
                 "Escape"
                 (on-select nil))))
           (.preventDefault event)))}
      (React/cloneElement input
                          #js {:autoComplete "off"
                               :aria-controls listbox-id
                               :aria-activedescendant (when active-item (get item->id active-item))})]
     [popup/Overlay
      {:open listbox-open
       :ref popup-ref
       :popper popper}
      [:div
       {:id listbox-id
        :style listbox-style
        :class ["nosco-dropdown" "nosco-select-list" "py-spacing-8"]
        :role "listbox"}
       (if (empty? items-local)
         [loading/LoadingNew]
         [:div {:style {:opacity (if loading 0.5 1)}}
          (for [[item id] items-with-ids]
            (render-item
             {:item item
              :id id
              :active (= active-item item)
              :on-select (fn [selected]
                           (when-not loading
                             (->items-local nil)
                             (on-select selected)))}))])]]]))

;; TODO perhaps these should be moved to util, and without the toast integration?

(defn copy-text-to-clipboard [text]
  (->
   (.writeText (.-clipboard js/navigator) text)
   (.then
    #(toast/show-confirmation {:title (t/t "Copied to clipboard")})
    #(toast/show-message-error {:title (t/t "Could not copy to clipboard")}))))

(defn copy-html-to-clipboard [html]
  (let [blob-html  (js/Blob. #js [html] #js {:type "text/html"})
        blob-text (js/Blob. #js [(html/html->text html)] #js {:type "text/plain"})
        polyfill-clipboard? (or (nil? js/window.ClipboardItem)
                                (nil? js/navigator.clipboard.write))
        ClipbordItemMaybePolyfilled (if polyfill-clipboard?
                                      clipboard-polyfill/ClipboardItem
                                      js/window.ClipboardItem)
        cliboard-write-maybe-polyfilled (if polyfill-clipboard?
                                          clipboard-polyfill/write
                                          #(.write js/navigator.clipboard %))
        clip-item (ClipbordItemMaybePolyfilled. #js {"text/html" blob-html
                                                     "text/plain" blob-text})]
    (->
     (cliboard-write-maybe-polyfilled #js [clip-item])
     (.then
      #(toast/show-confirmation {:title (t/t "Copied to clipboard")})
      #(toast/show-message-error {:title (t/t "Could not copy to clipboard")})))))

(defnc CopyButton [{:keys [text html]}]
  [:div
   [buttons2/Button {:variant buttons2/NewIcon
                     :size buttons2/SizeSmall
                     :icon icons2/Document-copy-small
                     :on-click
                     (if text
                       #(copy-text-to-clipboard text)
                       #(copy-html-to-clipboard (html/sanitize-html-editor html)))}]])

(defnc EditButton [{:keys [on-click]}]
  [buttons2/Button {:variant :text
                    :size :xsmall
                    :style {:white-space "nowrap"}
                    :on-click on-click
                    :label (t/t "Edit")}])

(defnc NumericStepper
  "A +/- stepper for integer values within a range.
   Matches the Figma Numeric Stepper component.

   Props:
   - :value       - current integer value
   - :set-value   - callback (new-value)
   - :min-val     - minimum allowed value
   - :max-val     - maximum allowed value
   - :label       - optional label text
   - :layout      - :inline (default) or :stacked
   - :unit        - optional unit text (only shown when layout is :stacked)
   - :disabled    - disable the stepper"
  [{:keys [value set-value min-val max-val label layout unit disabled]}]
  (let [stacked? (= layout :stacked)
        stepper-row
        [layouts/Row {:gap "8px" :class "align-items-center"}
         [buttons2/Button {:variant buttons2/NewDefault
                           :size buttons2/SizeSmall
                           :icon icons2/Minus-small
                           :disabled (or disabled (= value min-val))
                           :on-click #(set-value (max min-val (dec value)))}]
         [:div {:style {:width "32px"
                        :align-self "stretch"
                        :display "flex"
                        :align-items "center"
                        :justify-content "center"
                        :background-color colors/fill-subtler
                        :border (str "1px solid " colors/border-subtle)
                        :border-radius "8px"}}
          [text/Text {:variant text/Body-Md-Regular
                      :style {:text-align "center" :width "100%"}}
           (str value)]]
         [buttons2/Button {:variant buttons2/NewDefault
                           :size buttons2/SizeSmall
                           :icon icons2/Plus-small
                           :disabled (or disabled (= value max-val))
                           :on-click #(set-value (min max-val (inc value)))}]]]
    (cond
      ;; No label: just the stepper row
      (nil? label)
      stepper-row

      ;; Stacked with unit
      (and stacked? unit)
      [layouts/Column {:gap "8px"}
       [text/Text {:variant text/Label-Field-Label} label]
       [layouts/Row {:gap "8px" :class "align-items-center"}
        [text/Text {:variant text/Body-Sm-Regular
                    :class "text-neutral-500"
                    :truncate true
                    :style {:flex 1 :min-width 0}} unit]
        stepper-row]]

      ;; Stacked without unit
      stacked?
      [layouts/Column {:gap "8px"}
       [text/Text {:variant text/Label-Field-Label} label]
       stepper-row]

      ;; Inline (default)
      :else
      [layouts/Row {:gap "8px" :class "align-items-center"}
       [text/Text {:variant text/Label-Field-Label
                   :truncate true
                   :style {:flex 1 :min-width 0}} label]
       stepper-row])))

(defnc PasswordInput [{:keys [revealable] :as props} ref]
  {:wrap [(React/forwardRef)]}
  (let [[revealed ->revealed] (hooks/useState false)]
    [TextInput
     (assoc (dissoc props :revealable)
            :ref ref
            :type (if revealed "text" "password")
            :icon (when revealable
                    (hx/f
                     [:button
                      {:type "button"
                       :class "nosco-password-input-reveal"
                       :aria-label (t/t "Reveal password")
                       :title (t/t "Reveal password")
                       :aria-pressed revealed
                       :on-click #(->revealed not)}
                      [icons2/Icon
                       {:icon (if revealed
                                icons2/ViewShow-small
                                icons2/ViewHide-small)
                        :color colors/neutral-500}]])))]))
